import {
  Button,
  Flex,
  Text,
  FormField,
  FormLabel,
  FormControl,
  FormItem,
  TextEditor,
} from "@suns/design-system";
import { FormSelect } from "../FormSelect";
import { LoaderCircle } from "@suns/design-system/icons";
import { ReportFormSchema } from "../ReportForm";
import { DebouncedState } from "use-debounce";
import { ApolloGrade, ApolloGradeScope, ReportStatus } from "@suns/api";
import { ApolloGradeValueLabels } from "@/pages/reports/reports-const";
import MultiGameSelection from "../FormSections/MultiGameSelection";

interface SummerLeagueReportFormProps {
  readonly: boolean;
  report: ReportFormSchema;
  save: DebouncedState<(options: { publish: boolean }) => Promise<void>>;
  handlePublish: () => void;
  isPublishing: boolean;
  isSaving: boolean;
}

export function SummerLeagueReportForm(props: SummerLeagueReportFormProps) {
  const { isPublishing, isSaving, readonly, report, save, handlePublish } =
    props;

  return (
    <Flex direction="down" gap="lg">
      <Flex direction="down" gap="sm">
        <Text heading size="md" muted>
          Grade
        </Text>
        <FormSelect
          name={`apolloGrades.${ApolloGradeScope.CURRENT}`}
          placeholder="Select an option"
          options={[
            {
              value: ApolloGrade.G_LEAGUE,
              label: ApolloGradeValueLabels[ApolloGrade.G_LEAGUE],
            },
            {
              value: ApolloGrade.EXHIBIT_10,
              label: ApolloGradeValueLabels[ApolloGrade.EXHIBIT_10],
            },
            {
              value: ApolloGrade.TWO_WAY,
              label: ApolloGradeValueLabels[ApolloGrade.TWO_WAY],
            },
            {
              value: ApolloGrade.ROSTER,
              label: ApolloGradeValueLabels[ApolloGrade.ROSTER],
            },
          ]}
        />
      </Flex>

      <FormField
        name="offensiveNotes"
        render={({ field }) => (
          <FormItem>
            <Text size="md" muted heading asChild>
              <FormLabel>Offensive notes</FormLabel>
            </Text>
            <FormControl>
              <TextEditor
                readonly={readonly}
                className="h-44"
                {...field}
                defaultValue={report.offensiveNotes ?? undefined}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        name="defensiveNotes"
        render={({ field }) => (
          <FormItem>
            <Text size="md" muted heading asChild>
              <FormLabel>Defensive notes</FormLabel>
            </Text>
            <FormControl>
              <TextEditor
                readonly={readonly}
                className="h-44"
                {...field}
                defaultValue={report.defensiveNotes ?? undefined}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        name="otherNotes"
        render={({ field }) => (
          <FormItem>
            <Text size="md" muted heading asChild>
              <FormLabel>Other notes</FormLabel>
            </Text>
            <FormControl>
              <TextEditor
                readonly={readonly}
                className="h-44"
                {...field}
                defaultValue={report.otherNotes ?? undefined}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <MultiGameSelection
        gameId={report.games[0]?.id ?? undefined}
        readonly={readonly}
      />

      {!readonly ? (
        <Flex direction="right" gap="md">
          <FormField
            name="status"
            render={({ field }) => {
              return (
                <Button
                  type="button"
                  variant="muted"
                  disabled={isSaving || isPublishing}
                  onClick={handlePublish}
                >
                  {isPublishing ? (
                    <LoaderCircle size={18} className="animate-spin" />
                  ) : field?.value === ReportStatus.PUBLISHED ? (
                    "Unpublish"
                  ) : (
                    "Publish"
                  )}
                </Button>
              );
            }}
          />
          <Button
            type="button"
            disabled={isSaving || isPublishing}
            onClick={() => {
              save({ publish: false });
              save.flush();
            }}
          >
            {isSaving ? (
              <Flex direction="right" gap="sm" align="center">
                Saving...
                <LoaderCircle size={18} className="animate-spin" />
              </Flex>
            ) : (
              <Flex>Save</Flex>
            )}
          </Button>
        </Flex>
      ) : null}
    </Flex>
  );
}
